/**
 * @file fading-blur-mask.tsx
 * MD3 Expressive Fading Edge & Backdrop Blur Mask Primitive Component.
 *
 * Provides a hardware-accelerated linear gradient backdrop blur + surface tint overlay.
 */

import { useReducedMotion } from "motion/react";
import type * as React from "react";
import { cn } from "../../lib/utils";
import type { FadingBlurMaskProps } from "./fading-blur-mask.types";

/**
 * Gets the CSS linear-gradient string for the mask image based on direction.
 */
function getMaskGradient(direction: FadingBlurMaskProps["direction"]): string {
	switch (direction) {
		case "bottom":
			return "linear-gradient(to top, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 45%, rgba(0, 0, 0, 0.7) 75%, rgba(0, 0, 0, 0) 100%)";
		case "both":
			return "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 20%, rgba(0, 0, 0, 1) 40%, rgba(0, 0, 0, 1) 60%, rgba(0, 0, 0, 0.7) 80%, rgba(0, 0, 0, 0) 100%)";
		default:
			return "linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 45%, rgba(0, 0, 0, 0.7) 75%, rgba(0, 0, 0, 0) 100%)";
	}
}

/**
 * `<FadingBlurMask>` component renders a smooth gradient fading backdrop blur overlay.
 *
 * @example
 * ```tsx
 * <FadingBlurMask direction="top" blurIntensity={16} />
 * ```
 */
export function FadingBlurMask({
	direction = "top",
	blurIntensity = 12,
	blurHeight = "100%",
	color = "var(--md-sys-color-surface)",
	className,
	style,
	children,
}: FadingBlurMaskProps) {
	const shouldReduceMotion = useReducedMotion();
	const activeBlur = shouldReduceMotion ? 0 : blurIntensity;

	const maskGradient = getMaskGradient(direction);

	const maskStyle: React.CSSProperties = {
		position: "absolute",
		inset: 0,
		pointerEvents: "none",
		height: typeof blurHeight === "number" ? `${blurHeight}px` : blurHeight,
		backdropFilter: activeBlur > 0 ? `blur(${activeBlur}px)` : undefined,
		WebkitBackdropFilter: activeBlur > 0 ? `blur(${activeBlur}px)` : undefined,
		maskImage: maskGradient,
		WebkitMaskImage: maskGradient,
		backgroundColor: color,
		...style,
	};

	return (
		<div
			aria-hidden="true"
			className={cn(
				"absolute inset-0 pointer-events-none z-0 rounded-[inherit]",
				className,
			)}
			style={maskStyle}
		>
			{children}
		</div>
	);
}
